import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  approved: 'bg-green-100 text-green-800 border-green-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
  expired: 'bg-gray-100 text-gray-600 border-gray-200',
};

const TYPE_STYLES = {
  ticket: 'bg-blue-100 text-blue-700',
  quote: 'bg-purple-100 text-purple-700',
};

const ApprovalTable = ({ approvals, loading, onResend, onSendReminder, onViewDetails }) => {
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = [...(approvals ?? [])]?.sort((a, b) => {
    const av = a?.[sortKey] ?? '';
    const bv = b?.[sortKey] ?? '';
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const isOverdue = (row) => {
    if (row?.status !== 'pending') return false;
    return row?.deadline_at && new Date(row.deadline_at) < new Date();
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const SortIcon = ({ col }) => (
    <Icon
      name={sortKey === col ? (sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'}
      size={14}
      className="ml-1 inline-block text-muted-foreground"
    />
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!sorted?.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <Icon name="ClipboardCheck" size={40} className="mb-3 opacity-30" />
        <p className="font-medium">No approvals found</p>
        <p className="text-sm mt-1">Try adjusting your filters</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            {[
              { key: 'type', label: 'Type' },
              { key: 'customer_name', label: 'Customer' },
              { key: 'reference', label: 'Reference' },
              { key: 'created_at', label: 'Sent' },
              { key: 'deadline_at', label: 'Due Date' },
              { key: 'status', label: 'Status' },
            ]?.map(col => (
              <th
                key={col?.key}
                onClick={() => handleSort(col?.key)}
                className="px-4 py-3 text-left font-semibold text-muted-foreground cursor-pointer hover:text-foreground select-none whitespace-nowrap"
              >
                {col?.label}<SortIcon col={col?.key} />
              </th>
            ))}
            <th className="px-4 py-3 text-left font-semibold text-muted-foreground">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sorted?.map((row) => (
            <tr
              key={row?.id}
              className={`border-b border-border hover:bg-muted/20 transition-colors ${
                isOverdue(row) ? 'bg-red-50/40' : ''
              }`}
            >
              <td className="px-4 py-3">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${TYPE_STYLES?.[row?.type] ?? 'bg-gray-100 text-gray-700'}`}>
                  <Icon name={row?.type === 'ticket' ? 'ClipboardList' : 'FileText'} size={11} />
                  {row?.type === 'ticket' ? 'Ticket' : 'Quote'}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="font-medium text-foreground">{row?.customer_name ?? '—'}</div>
                {row?.customer_email && (
                  <div className="text-xs text-muted-foreground">{row?.customer_email}</div>
                )}
              </td>
              <td className="px-4 py-3">
                <span className="font-mono text-xs text-muted-foreground">{row?.reference ?? '—'}</span>
              </td>
              <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{formatDate(row?.created_at)}</td>
              <td className="px-4 py-3 whitespace-nowrap">
                {row?.deadline_at ? (
                  <span className={isOverdue(row) ? 'text-red-600 font-semibold' : 'text-muted-foreground'}>
                    {isOverdue(row) && <Icon name="AlertTriangle" size={12} className="inline mr-1" />}
                    {formatDate(row?.deadline_at)}
                  </span>
                ) : '—'}
              </td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${STATUS_STYLES?.[row?.status] ?? STATUS_STYLES?.expired}`}>
                  {row?.status?.charAt(0)?.toUpperCase() + row?.status?.slice(1)}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onViewDetails?.(row)}
                    title="View Details"
                    className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon name="Eye" size={15} />
                  </button>
                  {row?.status === 'pending' && (
                    <>
                      <button
                        onClick={() => onSendReminder?.(row)}
                        title="Send Reminder"
                        className="p-1.5 rounded-md hover:bg-amber-50 text-amber-600 hover:text-amber-700 transition-colors"
                      >
                        <Icon name="Bell" size={15} />
                      </button>
                      <button
                        onClick={() => onResend?.(row)}
                        title="Resend Approval"
                        className="p-1.5 rounded-md hover:bg-blue-50 text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        <Icon name="Send" size={15} />
                      </button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ApprovalTable;
